import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Bookmark, Trash2, Check, ChevronDown, ChevronUp, MessageCircle, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { MicButton } from "@/components/MicButton";

export function SavedQuestions() {
  const { token } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookmarks = async () => {
    try {
      const { data } = await axios.get(`${API}/question-bookmarks`, { headers });
      setBookmarks(data.bookmarks || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchBookmarks(); }, [token]);

  const deleteBookmark = async (id) => {
    try {
      await axios.delete(`${API}/question-bookmarks/${id}`, { headers });
      setBookmarks(bs => bs.filter(b => b.id !== id));
      toast.success("Question removed");
    } catch { toast.error("Failed to remove"); }
  };

  const updateReflection = async (id, reflection) => {
    try {
      await axios.patch(`${API}/question-bookmarks/${id}`, { reflection, answered: true }, { headers });
      setBookmarks(bs => bs.map(b => b.id === id ? { ...b, reflection, answered: true } : b));
      toast.success("Reflection saved");
    } catch { toast.error("Failed to save reflection"); }
  };

  const toggleAnswered = async (id, current) => {
    try {
      await axios.patch(`${API}/question-bookmarks/${id}`, { answered: !current }, { headers });
      setBookmarks(bs => bs.map(b => b.id === id ? { ...b, answered: !current } : b));
    } catch { toast.error("Failed to update"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (bookmarks.length === 0) {
    return (
      <div className="text-center py-12">
        <Bookmark className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
        <h4 className="text-sm font-medium text-muted-foreground">No saved questions yet</h4>
        <p className="text-xs text-muted-foreground/60 mt-1">When Omni asks you thought-provoking questions, save them here for later reflection.</p>
      </div>
    );
  }

  const unanswered = bookmarks.filter(b => !b.answered);
  const answered = bookmarks.filter(b => b.answered);

  return (
    <div data-testid="saved-questions" className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-heading font-semibold font-emphasis">Self-Reflection Prompts</h3>
        <span className="text-xs text-muted-foreground ml-auto">{unanswered.length} pending</span>
      </div>

      {/* Unanswered Questions */}
      {unanswered.map(b => (
        <QuestionCard
          key={b.id}
          bookmark={b}
          isExpanded={expandedId === b.id}
          onToggle={() => setExpandedId(expandedId === b.id ? null : b.id)}
          onDelete={() => deleteBookmark(b.id)}
          onSaveReflection={(text) => updateReflection(b.id, text)}
          onToggleAnswered={() => toggleAnswered(b.id, b.answered)}
        />
      ))}

      {/* Answered (collapsed) */}
      {answered.length > 0 && (
        <div className="pt-2 border-t border-border/30">
          <p className="text-xs text-muted-foreground/60 mb-2 flex items-center gap-1">
            <Check className="w-3 h-3" /> {answered.length} reflected on
          </p>
          {answered.map(b => (
            <QuestionCard
              key={b.id}
              bookmark={b}
              isExpanded={expandedId === b.id}
              onToggle={() => setExpandedId(expandedId === b.id ? null : b.id)}
              onDelete={() => deleteBookmark(b.id)}
              onSaveReflection={(text) => updateReflection(b.id, text)}
              onToggleAnswered={() => toggleAnswered(b.id, b.answered)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionCard({ bookmark, isExpanded, onToggle, onDelete, onSaveReflection, onToggleAnswered }) {
  const [draft, setDraft] = useState(bookmark.reflection || "");

  return (
    <div
      data-testid={`saved-question-${bookmark.id}`}
      className={cn(
        "rounded-xl border transition-all",
        bookmark.answered ? "border-border/20 bg-muted/10" : "border-primary/15 bg-card"
      )}
    >
      <div className="flex items-start gap-2 px-3 py-2.5 cursor-pointer" onClick={onToggle}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleAnswered(); }}
          className={cn(
            "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all",
            bookmark.answered
              ? "bg-primary border-primary text-primary-foreground"
              : "border-border/50 hover:border-primary"
          )}
        >
          {bookmark.answered && <Check className="w-3 h-3" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm leading-snug", bookmark.answered && "text-muted-foreground")}>{bookmark.question}</p>
          {bookmark.section && (
            <span className="text-[10px] text-muted-foreground/60 mt-0.5 block">{bookmark.section}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            data-testid={`delete-question-${bookmark.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded-full text-muted-foreground/30 hover:text-destructive hover:bg-destructive/5 transition-all"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border/20 space-y-2">
          <label className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Your reflection</label>
          <div className="flex items-start gap-1.5">
            <textarea
              data-testid={`reflection-input-${bookmark.id}`}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Take a moment to think about this..."
              rows={2}
              className="flex-1 text-sm bg-muted/20 border border-border/30 rounded-xl px-3 py-2 resize-none outline-none focus:border-primary/30"
            />
            <MicButton onTranscript={(text) => setDraft(d => d ? d + " " + text : text)} size="sm" />
          </div>
          {draft !== (bookmark.reflection || "") && (
            <button
              onClick={() => onSaveReflection(draft)}
              className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg hover:shadow-md transition-all"
            >
              Save reflection
            </button>
          )}
        </div>
      )}
    </div>
  );
}
